import { type FC, useMemo, useState } from 'react';

import classNames from 'classnames';

import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_SEGMENTS,
} from '@iconik-io/iconik-api/src/types/object_types';

import Option from '../../../../components/select_field_custom_options/option_description_component/option_description_component';
import { getMetadataCategoriesOfType } from '../../../../ducks/pages/admin_metadata_categories/thunks';
import type { CategoryObjectType } from '../../../../ducks/pages/admin_metadata_categories/types';
import { getCategoryLabel } from '../../../../ducks/pages/admin_metadata_categories/utils';
import { useAppDispatch } from '../../../../store/react_redux_typed';
import Label from '../../../label/label';
import { getSuffix } from '../../utils/utils';
import SelectFieldWrapper, {
  type SelectFieldWrapperProps,
} from '../select/select';

import styles from './select_metadata_category.module.css';

type LoadedOption = {
  label: string;
  value: string;
};

type Props = SelectFieldWrapperProps & {
  objectType: CategoryObjectType;
  onLoaded?: (options: LoadedOption[]) => void;
  hiddenWhenSingle: boolean;
  savedValue?: string;
  withAdditionalSegmentsCategories: boolean;
};

const SelectMetadataCategoryFieldWrapper: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const [optionsLength, setOptionsLength] = useState(0);

  const markedValueElements = useMemo(() => {
    if (!props.savedValue) {
      return undefined;
    }
    return {
      [props.savedValue]: <Label className={styles.saved_label}>Saved</Label>,
    };
  }, [props.savedValue]);

  return (
    <SelectFieldWrapper
      {...props}
      className={classNames(props.className, styles.select_field, {
        [styles.hidden]: props.hiddenWhenSingle && optionsLength <= 1,
      })}
      optionComponent={Option}
      preloadOptions
      loadOptions={() => {
        const promises = [];

        if (props.withAdditionalSegmentsCategories) {
          promises.push(
            dispatch(getMetadataCategoriesOfType(OBJECT_TYPE_ASSETS)),
          );
          promises.push(
            dispatch(getMetadataCategoriesOfType(OBJECT_TYPE_SEGMENTS)),
          );
        } else {
          promises.push(
            dispatch(getMetadataCategoriesOfType(props.objectType)),
          );
        }
        return Promise.all(promises).then(([categories, segmentCategories]) => {
          const allCategories = segmentCategories?.length
            ? [...categories, ...segmentCategories]
            : [...categories];
          const { onLoaded } = props;
          const options = allCategories
            .filter((category) => (category.view_ids?.length ?? 0) > 0)
            .map((category) => ({
              label:
                category.object_type === OBJECT_TYPE_SEGMENTS
                  ? category.label
                  : getCategoryLabel(category),
              value:
                category.object_type === OBJECT_TYPE_SEGMENTS
                  ? `${category.name}__suffix:segments`
                  : category.name,
            }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, {
                numeric: true,
                sensitivity: 'base',
              }),
            );
          onLoaded?.(options);
          setOptionsLength(options.length);
          return {
            options,
          };
        });
      }}
      placeholder="Select Metadata Category"
      markedValueElements={markedValueElements}
      optionSourceToSelect={(value) => ({
        value: value.value,
        label: value.label,
        suffix:
          getSuffix(value.value) === 'segments' ? (
            <Label className={styles.segments_label}>segments</Label>
          ) : undefined,
      })}
    />
  );
};

export default SelectMetadataCategoryFieldWrapper;
